/**
 * The registry of lead-capture websites.
 *
 * Generated from `pnpm discover:sites` against production on 2026-08-14 and
 * then reviewed by hand. Re-run that command after adding a site.
 *
 * Almost every site here shares one form template, so the normalizer's default
 * aliases already handle them: `firstName` → name, `machineType` → interest,
 * `sourcePage` → source URL, `utmSource`/`utmMedium`/`utmCampaign` → attribution.
 * Anything else the form captured (intent, placement, venue type, headcount…)
 * is picked up automatically as a qualifying detail and shown to the partner —
 * see `src/core/vocabulary.ts` for the labels and value translations.
 *
 * Adding a site is a code change on purpose: it is rare, it is worth reviewing,
 * and it belongs in version control next to the tests that cover it.
 */
import type { SiteDefinition } from "./types.ts";

export const SITES: SiteDefinition[] = [
  // --- the shared form template (firstName / machineType / intent / placement)
  {
    key: "vendingmaskin",
    name: "Vendingmaskin",
    collection: "vendingmaskin_leads",
  },
  {
    key: "snusautomat",
    name: "Snusautomat",
    collection: "snusautomat_leads",
  },
  {
    key: "pizzaautomat",
    name: "Pizzaautomat",
    collection: "pizzaautomat_leads",
  },
  {
    key: "tobaksautomater",
    name: "Tobaksautomater",
    collection: "tobaksautomater_leads",
  },
  {
    key: "obemannade",
    name: "Obemannade",
    collection: "obemannade_leads",
  },
  {
    key: "snacksautomat",
    name: "Snacksautomat",
    collection: "snacksautomat_leads",
  },
  {
    key: "obemannat24",
    name: "Obemannat24",
    collection: "obemannat24_leads",
  },
  {
    key: "gymautomater",
    name: "Gymautomater",
    collection: "gymautomater_leads",
  },
  {
    key: "padelautomater",
    name: "Padelautomater",
    collection: "padelautomater_leads",
  },
  {
    key: "skolautomater",
    name: "Skolautomater",
    collection: "skolautomater_leads",
  },
  {
    key: "dryckesautomat",
    name: "Dryckesautomat",
    collection: "dryckesautomat_leads",
  },
  {
    // Also collects orgNumber / employeeCount / role, which surface as details.
    key: "fikaautomat",
    name: "Fikaautomat",
    collection: "fikaautomat_leads",
  },

  // --- newer template (name / org / venueType / foodType …) -----------------
  {
    key: "matautomat",
    name: "Matautomat",
    collection: "matautomat_leads",
  },
  {
    key: "smartkyl",
    name: "Smartkyl",
    collection: "smartkyl_leads",
  },
  {
    key: "drickautomat",
    name: "Drickautomat",
    collection: "drickautomat_leads",
  },

  // --- third template (firstName / venueType / staffedHours + one site-specific
  // question each). Discovered 2026-08-20. No machineType or intent column, so
  // these leads carry no buy/lease/rent signal — that is the form, not us.
  {
    // Also asks for `rooms`.
    key: "hotellautomat",
    name: "Hotellautomat",
    collection: "hotellautomat_leads",
  },
  {
    // Also asks for `employees`.
    key: "kontorsautomat",
    name: "Kontorsautomat",
    collection: "kontorsautomat_leads",
  },
  {
    // Also asks for `season`.
    key: "glassautomat",
    name: "Glassautomat",
    collection: "glassautomat_leads",
  },
  {
    // Also asks for `sellsTcg` (trading-card games).
    key: "tcgautomat",
    name: "TCGautomat",
    collection: "tcgautomat_leads",
  },
];

/** Only sites that are currently switched on. */
export function activeSites(sites: readonly SiteDefinition[] = SITES): SiteDefinition[] {
  return sites.filter((site) => site.active !== false);
}

/**
 * Catches configuration mistakes at boot rather than at 2am: duplicate keys,
 * duplicate collections, or a site missing its identifiers.
 */
export function validateSites(sites: readonly SiteDefinition[] = SITES): string[] {
  const problems: string[] = [];
  const seenKeys = new Map<string, number>();
  const seenCollections = new Map<string, string>();

  sites.forEach((site, index) => {
    const label = site.key || `sites[${index}]`;

    if (!site.key) problems.push(`sites[${index}] is missing "key"`);
    if (!site.collection) problems.push(`${label} is missing "collection"`);
    if (!site.name) problems.push(`${label} is missing "name"`);

    if (site.key && !/^[a-z0-9][a-z0-9-]*$/.test(site.key)) {
      problems.push(`${label}: key must be lowercase letters, numbers and dashes`);
    }

    const previousIndex = seenKeys.get(site.key);
    if (previousIndex !== undefined) {
      problems.push(`duplicate site key "${site.key}" (sites[${previousIndex}] and sites[${index}])`);
    } else if (site.key) {
      seenKeys.set(site.key, index);
    }

    const previousSite = seenCollections.get(site.collection);
    if (previousSite !== undefined) {
      problems.push(
        `collection "${site.collection}" is claimed by both "${previousSite}" and "${site.key}"`,
      );
    } else if (site.collection) {
      seenCollections.set(site.collection, site.key);
    }
  });

  return problems;
}

export function findSiteByKey(key: string, sites: readonly SiteDefinition[] = SITES): SiteDefinition | null {
  return sites.find((site) => site.key === key) ?? null;
}

export function findSiteByCollection(
  collection: string,
  sites: readonly SiteDefinition[] = SITES,
): SiteDefinition | null {
  return sites.find((site) => site.collection === collection) ?? null;
}
